(() => {
  'use strict';
  if (window.LRSMapSolar) return;
  const rad = Math.PI / 180;
  const wrap = degrees => ((degrees + 180) % 360 + 360) % 360 - 180;

  // NOAA/Meeus solar declination and equation of time. The Date represents
  // one global instant; a visitor's time zone only changes its clock label.
  // https://gml.noaa.gov/grad/solcalc/calcdetails.html
  // https://gml.noaa.gov/grad/solcalc/main.js
  function sunPosition(date = new Date()) {
    const time = date instanceof Date ? date.getTime() : Number(date);
    if (!Number.isFinite(time)) return null;
    const t = (time / 86400000 + 2440587.5 - 2451545) / 36525;
    const meanLongitude = wrap(280.46646 + t * (36000.76983 + .0003032 * t)) * rad;
    const anomaly = (357.52911 + t * (35999.05029 - .0001537 * t)) * rad;
    const eccentricity = .016708634 - t * (.000042037 + .0000001267 * t);
    const center = Math.sin(anomaly) * (1.914602 - t * (.004817 + .000014 * t))
      + Math.sin(2 * anomaly) * (.019993 - .000101 * t) + Math.sin(3 * anomaly) * .000289;
    const omega = (125.04 - 1934.136 * t) * rad;
    const apparentLongitude = meanLongitude + (center - .00569 - .00478 * Math.sin(omega)) * rad;
    const seconds = 21.448 - t * (46.815 + t * (.00059 - .001813 * t));
    const obliquity = (23 + (26 + seconds / 60) / 60 + .00256 * Math.cos(omega)) * rad;
    const latitude = Math.asin(Math.sin(obliquity) * Math.sin(apparentLongitude)) / rad;
    const y = Math.tan(obliquity / 2) ** 2;
    const equationMinutes = 4 / rad * (y * Math.sin(2 * meanLongitude)
      - 2 * eccentricity * Math.sin(anomaly)
      + 4 * eccentricity * y * Math.sin(anomaly) * Math.cos(2 * meanLongitude)
      - .5 * y * y * Math.sin(4 * meanLongitude)
      - 1.25 * eccentricity * eccentricity * Math.sin(2 * anomaly));
    const minutesUTC = ((time % 86400000) + 86400000) % 86400000 / 60000;
    return {lat: latitude, lon: wrap((720 - minutesUTC - equationMinutes) / 4)};
  }

  // Natural Earth I: exactly the projection used by the land and city layers.
  function project(longitude, latitude) {
    const l = longitude * rad;
    const p = latitude * rad;
    const p2 = p * p;
    const p4 = p2 * p2;
    return [
      600 + 205 * l * (.8707 - .131979 * p2 + p4 * (-.013791 + p4 * (.003971 * p2 - .001529 * p4))),
      310 - 205 * p * (1.007226 + p2 * (.015085 + p4 * (-.044475 + .028874 * p2 - .005916 * p4)))
    ];
  }

  function clipLongitude(polygon, edge, direction) {
    const result = [];
    let a = polygon[polygon.length - 1];
    for (const b of polygon) {
      const aInside = (a[0] - edge) * direction >= 0;
      const bInside = (b[0] - edge) * direction >= 0;
      if (aInside !== bInside) {
        const amount = (edge - a[0]) / (b[0] - a[0]);
        result.push([edge, a[1] + amount * (b[1] - a[1])]);
      }
      if (bInside) result.push(b);
      a = b;
    }
    return result;
  }

  function nightPath(date = new Date(), altitude = 0) {
    const sun = sunPosition(date);
    const threshold = Number(altitude);
    if (!sun || !Number.isFinite(threshold)) return '';
    const target = Math.sin(Math.max(-90, Math.min(90, threshold)) * rad);
    const declination = sun.lat * rad;
    const sinDeclination = Math.sin(declination);
    const cosDeclination = Math.cos(declination);
    const midnight = wrap(sun.lon + 180);
    const halfWidth = latitude => {
      const p = latitude * rad;
      const denominator = Math.cos(p) * cosDeclination;
      const numerator = target - Math.sin(p) * sinDeclination;
      // At a pole the altitude is independent of longitude.
      if (Math.abs(denominator) < 1e-12) return numerator > 0 ? 180 : 0;
      const q = numerator / denominator;
      return q >= 1 ? 180 : q <= -1 ? 0 : Math.acos(-q) / rad;
    };
    const commands = [];
    // Half-degree bands follow both sides of the night cap. Each band is
    // clipped at the date line before projection; it can never fill the
    // opposite hemisphere when the terminator crosses the map edge or poles.
    let lowerWidth = halfWidth(-90);
    for (let lower = -90; lower < 90; lower += .5) {
      const upper = lower + .5;
      const upperWidth = halfWidth(upper);
      if (lowerWidth || upperWidth) {
        const band = [[midnight - lowerWidth, lower], [midnight + lowerWidth, lower],
          [midnight + upperWidth, upper], [midnight - upperWidth, upper]];
        for (let turn = -1; turn <= 1; turn++) {
          const shift = turn * 360;
          let polygon = clipLongitude(band, -180 + shift, 1);
          if (polygon.length) polygon = clipLongitude(polygon, 180 + shift, -1);
          if (polygon.length >= 3) {
            commands.push(polygon.map((point, index) => {
              const [x, y] = project(point[0] - shift, point[1]);
              return `${index ? 'L' : 'M'}${x.toFixed(2)},${y.toFixed(2)}`;
            }).join('') + 'Z');
          }
        }
      }
      lowerWidth = upperWidth;
    }
    return commands.join('');
  }

  window.LRSMapSolar = {sunPosition, nightPath};
})();
